import { Injectable } from '@angular/core';
import { EventType, NavigationEnd, Router } from '@angular/router';
import { RuntimeConfigService } from '@app/service/runtime-config.service';
import { ChatProviderHelper } from '@shared/constant/chat-const';
import { ChatIntegration } from '@shared/model/lender-config.model';
import { filter, skipWhile, take } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ChatIntegrationService {
  constructor(private readonly router: Router, private readonly runtimeConfigService: RuntimeConfigService) {}

  /** Master function that looks up a lenders chat configuration - provider and id (if not none).
   * Based on that configuration, the appropriate chat provider is initialized.
   * TODO: Ignore /admin routes - we should watch this to see if we need additional route logic in the future.
   * @params nothing
   * @returns nothing
   */
  public initialzeChatProvider(chatIntegration: ChatIntegration): void {
    // chatIntegration.provider has to be of string type ChatIntegrationProvider - defined in Admin Portal
    switch (chatIntegration?.provider) {
      case 'eltropy':
        // DEMO ID: https://arcos.uateltropy.com
        const esrc = this.runtimeConfigService.getConfig?.chatIntegration?.eltropySrc;
        const esnip = ChatProviderHelper.getEltropySnippet(chatIntegration.providerId, esrc);
        this.renderChatProviderScript(esnip, 'eltropy-snip');
        break;

      case 'liveperson':
        // DEMO ID: 83031622
        const lsnip = ChatProviderHelper.getLivePersonSnippet(chatIntegration.providerId);
        this.renderChatProviderScript(lsnip, 'live-snip');
        break;

      case 'glia':
        // NO DEMO ID - GLIA IS MANAGED VIA ORIGIN URL MAPPING IN GLIA ADMIN
        const gsrc = ChatProviderHelper.getGliaSrc();
        /**
         * This logic below is required because Glia requires additional consent - wait for
         * welcome page to be hit, and don't hit on public pages that don't require auth
         * In a future feature, we can add a cookie that identifies consent accepted
         * and simplify this logic a bit.
         */
        let flowHit = 1; // used to check if we were redirected, consented, and back to selection
        const handle = this.router.events
          .pipe(
            filter((r) => r.type === EventType.NavigationEnd),
            skipWhile((r: NavigationEnd) => {
              const path = r.urlAfterRedirects || r.url;
              const queryPoint = path.indexOf('?') > -1 ? path.indexOf('?') : path.length;
              // break into path segments and ignore query params
              const segments = path
                .substring(0, queryPoint)
                .split('/')
                .filter((s) => s !== '');
              const segment = segments[0];
              // if the first segment (or only) is one of these - don't activate glia since it can be hit
              // without consent being provided
              if (segment === 'app') {
                if (flowHit < 2 && ['ola-consumer-loan-type', 'membership'].includes(segments[segments.length - 1])) {
                  flowHit++;
                  // this is a default redirect route - assume a redirect into the workflow will happen
                  return true;
                }
                // this would be the first route of the application if splash screen is enabled
                return segments[segments.length - 1] === 'welcome';
              }

              return ['error', 'oops', 'user', 'terms-and-policy', 'admin', 'redirect', 'contact-us'].includes(segment);
            }),
            take(1)
          )
          .subscribe(() => {
            this.renderChatProviderCDN(gsrc, 'glia-src');
            handle.unsubscribe();
          });
        break;

      default:
        break;
    }
  }

  /**
   * Renders the chat provider's hard js snippet to the UI
   * @param snippet javascript snippet for the given chat provider
   */
  public renderChatProviderScript(snippet: string, id?: string): void {
    const script: HTMLScriptElement = document.createElement('script');
    script.async = true;
    script.text = snippet;
    if (id) {
      script.id = id;
    }
    document.body.appendChild(script);
  }

  /**
   * Renders the chat provider's CDN derived script to the UI
   * @param src The URL of the chat provider's script hosted on a CDN.
   */
  public renderChatProviderCDN(src: string, id?: string): void {
    const script: HTMLScriptElement = document.createElement('script');
    script.async = true;
    script.src = src;
    if (id) {
      script.id = id;
    }
    document.body.appendChild(script);
  }
}
